import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { SITE_ORIGIN, buildCanonical, isProductionOrigin } from "../lib/siteOrigin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TITLE = "Dr. Tania Gurdip Singh — Senior High-Risk Pregnancy Consultant";
const DEFAULT_DESC =
  "Evidence-based care for high-risk pregnancy and recurrent pregnancy loss. Preventing complications before they begin.";

// ---- Base SEO — accepts explicit props OR falls back to backend metadata
export function SEO({ title, description, image, canonical, type = "website",
                     jsonLd, noindex, breadcrumbs }) {
  const location = useLocation();
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    // Always fetch site-wide + route-specific JSON-LD from the backend.
    // If the caller has provided its own title/description, we simply
    // override those in the render below — but the backend response also
    // carries `MedicalOrganization`, `Physician`, and (for detail routes)
    // the content-specific block (`MedicalCondition` /
    // `MedicalScholarlyArticle`), all of which we still want on the page.
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/seo/page`, { params: { path: location.pathname } })
      .then(r => { if (!cancelled) setRemote(r.data); })
      .catch(() => { /* ignore, defaults will be used */ });
    return () => { cancelled = true; };
  }, [location.pathname]);

  const t = title || remote?.title || DEFAULT_TITLE;
  const d = description || remote?.description || DEFAULT_DESC;

  // ---- URL policy ------------------------------------------------------
  // Canonical URLs are ALWAYS built from the controlled production origin
  // (https://drtaniasingh.com) combined with the current normalised
  // pathname. Query strings and fragments are excluded. Any `canonical`
  // prop supplied by a page is normalised through the same helper so we
  // cannot accidentally emit a preview / localhost / IP URL.
  const canonicalPath = (() => {
    if (canonical) {
      try {
        // Accept either a full URL or a bare path from callers.
        const u = canonical.startsWith("http")
          ? new URL(canonical)
          : new URL(canonical, SITE_ORIGIN);
        return u.pathname;
      } catch {
        return location.pathname;
      }
    }
    return location.pathname;
  })();
  const url = buildCanonical(canonicalPath);

  // ---- Indexing policy -------------------------------------------------
  // Only the exact production ORIGIN (https:// + exact non-www hostname)
  // is allowed to be indexed. Any other combination — a mismatched
  // protocol (http:// on the same hostname), a mismatched hostname
  // (www., staging., preview host, localhost, IP) or the union of both —
  // must render noindex,nofollow. The explicit `noindex` prop still
  // forces noindex even on the production origin (admin/doctor pages).
  const production = isProductionOrigin();
  const robots = (production && !noindex) ? "index, follow" : "noindex, nofollow";

  const img = image || remote?.image || null;
  // Merge JSON-LD: caller-provided always wins on duplicate @types.
  // Detail pages (Condition/Article) supply their own `breadcrumbs` prop,
  // in which case we drop the backend-generated BreadcrumbList to avoid
  // emitting two identical blocks.
  const remoteLd = (remote?.jsonld || []).filter(
    x => !(breadcrumbs && x?.["@type"] === "BreadcrumbList")
  );
  const allLd = [...remoteLd];
  if (jsonLd) {
    (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach(x => allLd.push(x));
  }
  if (breadcrumbs && breadcrumbs.length > 1) {
    allLd.push({
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem", position: i + 1, name: b.name,
        item: b.item?.startsWith("http") ? b.item : buildCanonical(b.item || "/")
      })),
    });
  }

  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d}/>
      <meta name="robots" content={robots}/>
      <link rel="canonical" href={url}/>
      {/* Open Graph */}
      <meta property="og:title" content={t}/>
      <meta property="og:description" content={d}/>
      <meta property="og:type" content={type}/>
      <meta property="og:url" content={url}/>
      <meta property="og:site_name" content="Dr. Tania Gurdip Singh"/>
      {img && <meta property="og:image" content={img}/>}
      {/* Twitter */}
      <meta name="twitter:card" content={img ? "summary_large_image" : "summary"}/>
      <meta name="twitter:title" content={t}/>
      <meta name="twitter:description" content={d}/>
      {img && <meta name="twitter:image" content={img}/>}
      {allLd.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

// Convenience wrapper for admin/doctor pages that should never be indexed.
export function NoIndex({ title }) {
  return (
    <Helmet>
      {title && <title>{title}</title>}
      <meta name="robots" content="noindex, nofollow"/>
    </Helmet>
  );
}
